import FlexSearch from 'flexsearch'
import { useMemo, useState } from 'react'

interface UseFlexSearchOptions<T> {
  data: T[]
  searchFields: (keyof T)[]
  idKey: keyof T
  threshold?: number
  fieldWeights?: Partial<Record<keyof T, number>>
}

// SearchResult<T>가 T를 확장하도록 변경
// score는 선택적 프로퍼티
// T가 객체 타입임을 보장하기 위해 extends object 추가
export type SearchResult<T extends object> = T & { score?: number }

export const useFlexSearch = <T extends object>({ data, searchFields, idKey, fieldWeights }: UseFlexSearchOptions<T>) => {
  const [searchResults, setSearchResults] = useState<SearchResult<T>[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const searchIndex = useMemo(() => {
    const index = new FlexSearch.Index({
      tokenize: 'forward',
      resolution: 9
    })

    data.forEach((item) => {
      const searchText = searchFields
        .map((field) => {
          const value = item[field]
          if (typeof value === 'string') {
            return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
          }

          return ''
        })
        .join(' ')
      index.add(String(item[idKey]), searchText)
    })

    return index
  }, [data, searchFields, idKey])

  const dataMap = useMemo(() => {
    const map = new Map<string, T>()
    data.forEach((item) => {
      map.set(String(item[idKey]), item)
    })

    return map
  }, [data, idKey])

  const search = async (query: string): Promise<SearchResult<T>[]> => {
    // 한글 자모(초성/중성/종성)만으로 이루어진 문자열인지 체크하는 함수
    function isOnlyHangulJamo(str: string) {
        // 초성(ㄱ~ㅎ): \u3131-\u314e
      // 중성(ㅏ~ㅣ): \u314f-\u3163
      // 기타 자모: \u3165-\u3186
      // 전체 자모: \u3131-\u3186
      return /^[\u3131-\u3186]+$/.test(str.replace(/\s/g, ''));
    }

    if (!query.trim() || isOnlyHangulJamo(query)) {
      setSearchResults([])

      return []
    }

    setIsSearching(true)

    try {
      const results = await searchIndex.search(query, {
        limit: 100
      })

      // FlexSearch의 결과 id는 string 또는 number일 수 있으므로 string으로 변환
      let searchData: SearchResult<T>[] = (results as (string | number)[])
        .map((id) => {
          const item = dataMap.get(String(id))

          return item ? { ...item } as SearchResult<T> : null
        })
        .filter((item): item is SearchResult<T> => item !== null)

      if (fieldWeights) {
        searchData = searchData
          .map((item) => {
            let score = 0
            for (const field of Object.keys(fieldWeights) as (keyof T)[]) {
              const weight = fieldWeights[field] || 0
              const value = item[field]
              if (typeof value === 'string' && value.includes(query)) {
                score += weight
              }
            }

            return { ...item, score }
          })
          .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
      }

      setSearchResults(searchData)

      return searchData
    } catch (error) {
      console.error('검색 중 오류가 발생했습니다:', error)
      setSearchResults([])

      return []
    } finally {
      setIsSearching(false)
    }
  }

  const clearSearch = () => {
    setSearchResults([])
  }

  return {
    search,
    searchResults,
    isSearching,
    clearSearch
  }
} 